import { z } from 'zod';
import { caseStatusSchema } from '../case/case.js';
import { codeSchema } from '../code.js';
/**
 * Workflow definition model. A workflow is a directed graph stored as
 * JSON inside an immutable published version. Cases pin the version they started with.
 */
export const conditionSchema = z.lazy(() => z.discriminatedUnion('op', [
    z.object({ op: z.literal('and'), args: z.array(conditionSchema).min(1) }),
    z.object({ op: z.literal('or'), args: z.array(conditionSchema).min(1) }),
    z.object({ op: z.literal('not'), arg: conditionSchema }),
    z.object({ op: z.literal('eq'), field: z.string(), value: z.unknown() }),
    z.object({ op: z.literal('neq'), field: z.string(), value: z.unknown() }),
    z.object({ op: z.literal('in'), field: z.string(), value: z.array(z.unknown()) }),
    z.object({ op: z.literal('gt'), field: z.string(), value: z.number() }),
    z.object({ op: z.literal('gte'), field: z.string(), value: z.number() }),
    z.object({ op: z.literal('lt'), field: z.string(), value: z.number() }),
    z.object({ op: z.literal('lte'), field: z.string(), value: z.number() }),
    z.object({ op: z.literal('exists'), field: z.string() }),
    z.object({ op: z.literal('true') }),
]));
/** Human action catalog. Codes are stable; labels live in the actions table. */
export const HumanActionCode = {
    RESPOND: 'RESPOND',
    REVIEW: 'REVIEW',
    REJECT: 'REJECT',
    SIGN: 'SIGN',
    REQUEST_INFORMATION: 'REQUEST_INFORMATION',
    REQUEST_CORRECTION: 'REQUEST_CORRECTION',
    ASSIGN: 'ASSIGN',
    REASSIGN: 'REASSIGN',
    ACKNOWLEDGE: 'ACKNOWLEDGE',
    ESCALATE: 'ESCALATE',
    TRANSFER: 'TRANSFER',
    ARCHIVE: 'ARCHIVE',
    CLOSE: 'CLOSE',
    REVIEW_CLASSIFICATION: 'REVIEW_CLASSIFICATION',
    GENERIC: 'GENERIC',
    /** Requerimientos judiciales: registrar el auto (juzgado, radicado, término), esperar la decisión del juez, cumplir o impugnar el fallo. */
    REGISTER_COURT_ORDER: 'REGISTER_COURT_ORDER',
    AWAIT_COURT: 'AWAIT_COURT',
    COMPLY_RULING: 'COMPLY_RULING',
};
export const SystemActionCode = {
    CLASSIFY: 'CLASSIFY',
    COMPUTE_DUE_DATE: 'COMPUTE_DUE_DATE',
    GENERATE_RADICATION: 'GENERATE_RADICATION',
    SEND_NOTIFICATION: 'SEND_NOTIFICATION',
    GENERATE_DOCUMENT: 'GENERATE_DOCUMENT',
    CREATE_TASK: 'CREATE_TASK',
    SET_PRIORITY: 'SET_PRIORITY',
    SET_STATUS: 'SET_STATUS',
    AUTO_ASSIGN: 'AUTO_ASSIGN',
    NOTIFY_CITIZEN: 'NOTIFY_CITIZEN',
    /** Requerimientos judiciales: leer el auto (sugerencias para «Registrar requerimiento») y enviar al despacho del caso. */
    READ_COURT_ORDER: 'READ_COURT_ORDER',
    SEND_TO_COURT: 'SEND_TO_COURT',
};
/** Who should do a human task. Never a specific person. */
export const actorRuleSchema = z.object({
    /** Obsoleto: los flujos ya no nombran roles (migración 0007). Se acepta en definiciones viejas y se ignora. */
    roleCode: z.string().optional(),
    organizationalUnitId: z.string().uuid().optional(),
    /** 'case' = use the specialty decided for the case; 'fixed' = specialtyId below. */
    specialtySource: z.enum(['case', 'fixed', 'none']).default('case'),
    specialtyId: z.string().uuid().optional(),
    /**
     * auto: the Assignment Engine picks someone of the case area (or the rule's area) by specialty, capacity and load.
     * case_assignee: the current responsible of the case.
     * approver: someone who approves in the responsible's area or, climbing the org chart, in the nearest area above
     *   (`organizational_unit_members.approves`); among several at the same area, the least loaded available one.
     * signer: the same with `signs`. When the person who approved the response can also sign there, they sign it.
     * triage_team: the Assignment Engine among the triage people (`intake.triageUserIds`; triage only). The name is
     *   historical: there are no teams any more, and published definitions keep the value.
     * case_assignee_supervisor / area_leader: obsolete (they followed the area leaders). Older definitions keep working;
     *   the designer no longer offers them.
     */
    strategy: z.enum(['auto', 'case_assignee', 'approver', 'signer', 'case_assignee_supervisor', 'area_leader', 'triage_team']).default('auto'),
});
export const JoinMode = { ALL: 'ALL', ANY: 'ANY', N_OF_M: 'N_OF_M' };
const baseNode = z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(120),
    position: z.object({ x: z.number(), y: z.number() }).default({ x: 0, y: 0 }),
    /** Status the case takes when this node becomes active. */
    caseStatus: caseStatusSchema.optional(),
    description: z.string().max(500).optional(),
});
export const workflowNodeSchema = z.discriminatedUnion('type', [
    baseNode.extend({ type: z.literal('start') }),
    baseNode.extend({ type: z.literal('end') }),
    baseNode.extend({
        type: z.literal('human_task'),
        actionCode: z.string().min(1),
        actorRule: actorRuleSchema,
        slaPolicyCode: z.string().optional(),
        /**
         * Vencimiento tomado de un dato del caso en vez de contar la política: una ruta del contexto de condiciones
         * (`case.metadata.judicial.deadlineAt`, `form.deadlineAt`). La política, si la hay, pone solo los escalamientos.
         */
        dueFromField: z.string().max(120).optional(),
        formCode: z.string().optional(),
        /** Possible outcomes the actor can pick; edges route by outcome. Default ['DONE']. */
        outcomes: z.array(z.string().min(1)).min(1).default(['DONE']),
        requiresSignature: z.boolean().default(false),
        /** Set when this node is a multi-approver task rendered as N parallel tasks. */
        approval: z
            .object({ mode: z.enum(['ALL', 'ANY', 'N_OF_M']), n: z.number().int().min(1).optional() })
            .optional(),
        config: z.record(z.unknown()).optional(),
    }),
    baseNode.extend({
        type: z.literal('system_task'),
        actionCode: z.string().min(1),
        config: z.record(z.unknown()).optional(),
    }),
    baseNode.extend({ type: z.literal('decision') }),
    baseNode.extend({ type: z.literal('parallel') }),
    baseNode.extend({
        type: z.literal('join'),
        mode: z.enum(['ALL', 'ANY', 'N_OF_M']).default('ALL'),
        n: z.number().int().min(1).optional(),
    }),
    /**
     * Terminal node of the triage workflow: hands the case over to the workflow of the type the
     * classification settled on (case type default → a workflow listing the type → general workflow).
     */
    baseNode.extend({ type: z.literal('handoff') }),
    baseNode.extend({
        type: z.literal('timer'),
        /** Wait this long (business or calendar) before continuing. */
        durationMinutes: z.number().int().min(1),
        timeMode: z.enum(['CALENDAR', 'BUSINESS']).default('CALENDAR'),
    }),
]);
export const workflowEdgeSchema = z.object({
    id: z.string().min(1),
    from: z.string().min(1),
    to: z.string().min(1),
    label: z.string().max(80).optional(),
    /** For human_task sources: which outcome triggers this edge. */
    outcome: z.string().optional(),
    /** For decision sources: condition evaluated against the case context. */
    condition: conditionSchema.optional(),
    /** For decision sources: taken when no conditional edge matches. */
    isDefault: z.boolean().default(false),
});
export const workflowDefinitionSchema = z.object({
    nodes: z.array(workflowNodeSchema).min(2),
    edges: z.array(workflowEdgeSchema),
    /** Optional overall SLA for the whole case. */
    slaPolicyCode: z.string().optional(),
});
export const WorkflowVersionStatus = {
    DRAFT: 'DRAFT',
    TESTING: 'TESTING',
    PUBLISHED: 'PUBLISHED',
    DEPRECATED: 'DEPRECATED',
    ARCHIVED: 'ARCHIVED',
};
/** The triage is a system workflow: maintained by the platform, hidden from the designer and never edited by tenants. */
export const TRIAGE_WORKFLOW_CODE = 'TRIAJE';
/**
 * Flujos base de la entidad (los crea el seed desde su plantilla): no se eliminan y se pueden restablecer a la
 * definición inicial (`POST /workflows/:id/restore-base`) si se modificaron mal o ya no sirven como quedaron.
 */
export const BASE_WORKFLOW_CODES = ['RESPUESTA_CON_APROBACION', 'TUTELA'];
export const isBaseWorkflow = (code) => BASE_WORKFLOW_CODES.includes(code);
export const TRIAGE_ONLY_ACTIONS = [SystemActionCode.CLASSIFY, HumanActionCode.REVIEW_CLASSIFICATION];
/** Static validation run before publishing. Pure function so the designer can run it too. */
export function validateWorkflowDefinition(def, opts = {}) {
    const issues = [];
    const ids = new Set();
    for (const n of def.nodes) {
        if (ids.has(n.id))
            issues.push({ level: 'error', code: 'DUPLICATE_NODE_ID', message: `Nodo duplicado: ${n.id}`, nodeId: n.id });
        ids.add(n.id);
    }
    const starts = def.nodes.filter((n) => n.type === 'start');
    const ends = def.nodes.filter((n) => n.type === 'end');
    if (starts.length !== 1)
        issues.push({ level: 'error', code: 'START_COUNT', message: 'Debe existir exactamente un nodo de inicio' });
    if (ends.length === 0 && !def.nodes.some((n) => n.type === 'handoff'))
        issues.push({ level: 'error', code: 'NO_END', message: 'Debe existir al menos un nodo de fin o de derivación' });
    const outgoing = new Map();
    const incoming = new Map();
    for (const e of def.edges) {
        if (!ids.has(e.from) || !ids.has(e.to)) {
            issues.push({ level: 'error', code: 'DANGLING_EDGE', message: `Conexión inválida ${e.from} → ${e.to}`, edgeId: e.id });
            continue;
        }
        outgoing.set(e.from, [...(outgoing.get(e.from) ?? []), e]);
        incoming.set(e.to, [...(incoming.get(e.to) ?? []), e]);
    }
    // Reachability from start
    const reachable = new Set();
    const start = starts[0];
    if (start) {
        const stack = [start.id];
        while (stack.length) {
            const cur = stack.pop();
            if (reachable.has(cur))
                continue;
            reachable.add(cur);
            for (const e of outgoing.get(cur) ?? [])
                stack.push(e.to);
        }
    }
    for (const n of def.nodes) {
        const outs = outgoing.get(n.id) ?? [];
        const ins = incoming.get(n.id) ?? [];
        if (!reachable.has(n.id))
            issues.push({ level: 'error', code: 'ORPHAN_NODE', message: `Nodo "${n.name}" no es alcanzable`, nodeId: n.id });
        const terminal = n.type === 'end' || n.type === 'handoff';
        if (!terminal && outs.length === 0)
            issues.push({ level: 'error', code: 'NO_OUTGOING', message: `Nodo "${n.name}" no tiene salida`, nodeId: n.id });
        if (n.type !== 'start' && ins.length === 0)
            issues.push({ level: 'error', code: 'NO_INCOMING', message: `Nodo "${n.name}" no tiene entrada`, nodeId: n.id });
        if (n.type === 'end' && outs.length > 0)
            issues.push({ level: 'error', code: 'END_HAS_OUTGOING', message: `El fin "${n.name}" no puede tener salidas`, nodeId: n.id });
        if (n.type === 'handoff' && outs.length > 0)
            issues.push({ level: 'error', code: 'HANDOFF_HAS_OUTGOING', message: `La derivación "${n.name}" no puede tener salidas: el caso sigue en el flujo de su tipo`, nodeId: n.id });
        if (n.type === 'human_task') {
            for (const o of n.outcomes) {
                if (!outs.some((e) => e.outcome === o || (!e.outcome && n.outcomes.length === 1)))
                    issues.push({ level: 'error', code: 'OUTCOME_UNROUTED', message: `Resultado "${o}" de "${n.name}" no tiene conexión`, nodeId: n.id });
            }
        }
        if (n.type === 'decision') {
            if (outs.length < 2)
                issues.push({ level: 'error', code: 'DECISION_NEEDS_BRANCHES', message: `La decisión "${n.name}" necesita al menos dos salidas`, nodeId: n.id });
            if (!outs.some((e) => e.isDefault) && !outs.every((e) => e.condition))
                issues.push({ level: 'error', code: 'DECISION_NO_DEFAULT', message: `La decisión "${n.name}" necesita una salida por defecto`, nodeId: n.id });
        }
        if (n.type === 'join' && n.mode === 'N_OF_M' && (!n.n || n.n > ins.length))
            issues.push({ level: 'error', code: 'JOIN_N_INVALID', message: `El join "${n.name}" tiene N inválido`, nodeId: n.id });
        if (opts.role === 'type') {
            if (n.type === 'handoff')
                issues.push({ level: 'error', code: 'HANDOFF_OUTSIDE_TRIAGE', message: `"${n.name}" solo puede estar en el flujo de triaje`, nodeId: n.id });
            if ((n.type === 'system_task' || n.type === 'human_task') && TRIAGE_ONLY_ACTIONS.includes(n.actionCode))
                issues.push({ level: 'warning', code: 'CLASSIFY_OUTSIDE_TRIAGE', message: `"${n.name}" sobra: el triaje ya clasificó el caso antes de llegar a este flujo`, nodeId: n.id });
        }
    }
    if (opts.role === 'triage' && !def.nodes.some((n) => n.type === 'handoff'))
        issues.push({ level: 'warning', code: 'TRIAGE_NO_HANDOFF', message: 'El triaje no tiene «Derivar al flujo del tipo»: los casos no pasarán al flujo de su tipo' });
    return issues;
}
export const createWorkflowSchema = z.object({
    code: codeSchema(60, 2),
    name: z.string().min(2).max(120),
    description: z.string().max(500).optional(),
    caseTypeCodes: z.array(z.string()).default([]),
    templateCode: z.string().optional(),
});
/** POST /cases/:id/reroute: moves an open case to another workflow on purpose (never automatic). */
export const rerouteCaseSchema = z.object({
    reason: z.string().trim().min(5).max(1000),
    /** Target workflow; omitted = the workflow of the case's current type. */
    workflowCode: z.string().min(1).max(60).optional(),
});
export const saveWorkflowVersionSchema = z.object({
    definition: workflowDefinitionSchema,
    changelog: z.string().max(1000).optional(),
});
export const TaskStatus = {
    PENDING: 'PENDING',
    IN_PROGRESS: 'IN_PROGRESS',
    BLOCKED: 'BLOCKED',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
};
/**
 * Firma de un paso con «Requiere firma»: el código de 6 dígitos de la app autenticadora de quien firma (si tiene la
 * verificación en dos pasos activa) o, de respaldo, el que le llegó por correo (`challengeId` del envío).
 */
export const SIGNATURE_CODE_TTL_MINUTES = 10;
export const SIGNATURE_CODE_MAX_ATTEMPTS = 5;
export const SIGNATURE_CODE_RESEND_SECONDS = 60;
export const signatureInputSchema = z.object({
    /** Sin `challengeId`: el código es el de la app autenticadora. */
    challengeId: z.string().uuid().optional(),
    code: z.string().regex(/^\d{6}$/, 'El código tiene 6 dígitos'),
});
export const completeTaskSchema = z.object({
    outcome: z.string().min(1).default('DONE'),
    formData: z.record(z.unknown()).optional(),
    comment: z.string().max(5000).optional(),
    /** Obligatoria en un paso que requiere firma (salvo al rechazar). */
    signature: signatureInputSchema.optional(),
});
/**
 * Si el paso «Notificar al ciudadano» adjunta lo que se recibió al radicar (constancia, petición sellada y anexos; por
 * correo, el PDF del correo): `config.attachReceived`. Un paso que no lo dice lo hace solo con la plantilla del acuse
 * (`NOTIFY_RECEIVED`), como antes de existir el interruptor.
 */
export const attachesReceived = (config) => typeof config?.attachReceived === 'boolean' ? config.attachReceived : config?.templateCode === 'NOTIFY_RECEIVED';
//# sourceMappingURL=workflow.js.map